import React, { useMemo, useState, useEffect } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Button } from "../ui/button";
import { Separator } from "../ui/separator";
import { useArchivalMode } from "@/contexts/ArchivalModeContext";
import { FileText, ExternalLink, FileSignature } from "lucide-react";
import PlanningSignoffDialog, { PlanningSignoffTokens } from "../planning/PlanningSignoffDialog";
import { useAuth } from "@/contexts/AuthContext";
import { useEngagement } from "@/contexts/EngagementContext";
import { apiService } from "@/services/api-service";
import { loadMiscSignoffs, getSignoffEntriesForCard, type MiscSignoffEntry } from "@/utils/misc-signoff-utils";
import { SHAREPOINT_FY_YEAR } from "@/constants/sharepoint-config";
import CAROClauseIChecklistModal from "./CAROClauseIChecklistModal";
import CAROClauseIIChecklistModal from "./CAROClauseIIChecklistModal";
import CAROClauseIIIChecklistModal from "./CAROClauseIIIChecklistModal";
import CAROClauseIVChecklistModal from "./CAROClauseIVChecklistModal";
import CAROClauseVChecklistModal from "./CAROClauseVChecklistModal";
import CAROClauseVIChecklistModal from "./CAROClauseVIChecklistModal";
import CAROClauseVIIChecklistModal from "./CAROClauseVIIChecklistModal";
import CAROClauseVIIIChecklistModal from "./CAROClauseVIIIChecklistModal";
import CAROClauseIXChecklistModal from "./CAROClauseIXChecklistModal";
import CAROClauseXChecklistModal from "./CAROClauseXChecklistModal";
import CAROClauseXIChecklistModal from "./CAROClauseXIChecklistModal";
import CAROClauseXIIChecklistModal from "./CAROClauseXIIChecklistModal";
import CAROClauseXIIIChecklistModal from "./CAROClauseXIIIChecklistModal";
import CAROClauseXIVChecklistModal from "./CAROClauseXIVChecklistModal";
import CAROClauseXVChecklistModal from "./CAROClauseXVChecklistModal";
import CAROClauseXVIChecklistModal from "./CAROClauseXVIChecklistModal";
import CAROClauseXVIIChecklistModal from "./CAROClauseXVIIChecklistModal";
import CAROClauseXVIIIChecklistModal from "./CAROClauseXVIIIChecklistModal";
import CAROClauseXIXChecklistModal from "./CAROClauseXIXChecklistModal";
import CAROClauseXXChecklistModal from "./CAROClauseXXChecklistModal";

interface CAROLandingProps {
  setActiveSection: (section: string) => void;
}

const WORKSPACE_NAME = "CARO";

export default function CAROLanding({ setActiveSection }: CAROLandingProps) {
  const { isArchivalMode } = useArchivalMode();
  const { user } = useAuth();
  const { currentEngagement } = useEngagement();
  const [userEngagementRole, setUserEngagementRole] = useState<string | null>(null);
  const [signoffDialogOpen, setSignoffDialogOpen] = useState(false);
  const [cardSignoffs, setCardSignoffs] = useState<MiscSignoffEntry[]>([]);
  const [applicability, setApplicability] = useState({
    banking: "yes",
    insurance: "yes",
    section8: "yes",
    opc: "yes",
    privateSmall: "yes",
  });

  const allNo = useMemo(() => {
    return Object.values(applicability).every((v) => v === "no");
  }, [applicability]);

  const [checklistOpened, setChecklistOpened] = useState(false);
  const [loadingCAROData, setLoadingCAROData] = useState(false);
  const [existingCAROReport, setExistingCAROReport] = useState<{ name: string; url: string } | null>(null);
  const [clauseIChecklistOpen, setClauseIChecklistOpen] = useState(false);
  const [clauseIResponses, setClauseIResponses] = useState<Record<string, string>>({});
  const [clauseIIChecklistOpen, setClauseIIChecklistOpen] = useState(false);
  const [clauseIIResponses, setClauseIIResponses] = useState<Record<string, string>>({});
  const [clauseIIIChecklistOpen, setClauseIIIChecklistOpen] = useState(false);
  const [clauseIIIResponses, setClauseIIIResponses] = useState<Record<string, string>>({});
  const [clauseIVChecklistOpen, setClauseIVChecklistOpen] = useState(false);
  const [clauseIVResponses, setClauseIVResponses] = useState<Record<string, string>>({});
  const [clauseVChecklistOpen, setClauseVChecklistOpen] = useState(false);
  const [clauseVResponses, setClauseVResponses] = useState<Record<string, string>>({});
  const [clauseVIChecklistOpen, setClauseVIChecklistOpen] = useState(false);
  const [clauseVIResponses, setClauseVIResponses] = useState<Record<string, string>>({});
  const [clauseVIIChecklistOpen, setClauseVIIChecklistOpen] = useState(false);
  const [clauseVIIResponses, setClauseVIIResponses] = useState<Record<string, string>>({});
  const [clauseVIIIChecklistOpen, setClauseVIIIChecklistOpen] = useState(false);
  const [clauseVIIIResponses, setClauseVIIIResponses] = useState<Record<string, string>>({});
  const [clauseIXChecklistOpen, setClauseIXChecklistOpen] = useState(false);
  const [clauseIXResponses, setClauseIXResponses] = useState<Record<string, string>>({});
  const [clauseXChecklistOpen, setClauseXChecklistOpen] = useState(false);
  const [clauseXResponses, setClauseXResponses] = useState<Record<string, string>>({});
  const [clauseXIChecklistOpen, setClauseXIChecklistOpen] = useState(false);
  const [clauseXIResponses, setClauseXIResponses] = useState<Record<string, string>>({});
  const [clauseXIIChecklistOpen, setClauseXIIChecklistOpen] = useState(false);
  const [clauseXIIResponses, setClauseXIIResponses] = useState<Record<string, string>>({});
  const [clauseXIIIChecklistOpen, setClauseXIIIChecklistOpen] = useState(false);
  const [clauseXIIIResponses, setClauseXIIIResponses] = useState<Record<string, string>>({});
  const [clauseXIVChecklistOpen, setClauseXIVChecklistOpen] = useState(false);
  const [clauseXIVResponses, setClauseXIVResponses] = useState<Record<string, string>>({});
  const [clauseXVChecklistOpen, setClauseXVChecklistOpen] = useState(false);
  const [clauseXVResponses, setClauseXVResponses] = useState<Record<string, string>>({});
  const [clauseXVIChecklistOpen, setClauseXVIChecklistOpen] = useState(false);
  const [clauseXVIResponses, setClauseXVIResponses] = useState<Record<string, string>>({});
  const [clauseXVIIChecklistOpen, setClauseXVIIChecklistOpen] = useState(false);
  const [clauseXVIIResponses, setClauseXVIIResponses] = useState<Record<string, string>>({});
  const [clauseXVIIIChecklistOpen, setClauseXVIIIChecklistOpen] = useState(false);
  const [clauseXVIIIResponses, setClauseXVIIIResponses] = useState<Record<string, string>>({});
  const [clauseXIXChecklistOpen, setClauseXIXChecklistOpen] = useState(false);
  const [clauseXIXResponses, setClauseXIXResponses] = useState<Record<string, string>>({});
  const [clauseXXChecklistOpen, setClauseXXChecklistOpen] = useState(false);
  const [clauseXXResponses, setClauseXXResponses] = useState<Record<string, string>>({});

  // Track which clauses have responses
  const [clauseCompletionStatus, setClauseCompletionStatus] = useState<Record<string, boolean>>({});

  const [clauseApplicability, setClauseApplicability] = useState<Record<string, string>>({
    i: "no",
    ii: "no",
    iii: "no",
    iv: "no",
    v: "no",
    vi: "no",
    vii: "no",
    viii: "no",
    ix: "no",
    x: "no",
    xi: "yes", // always applicable
    xii: "no",
    xiii: "no",
    xiv: "no",
    xv: "yes", // always applicable
    xvi: "no",
    xvii: "yes", // always applicable
    xviii: "no",
    xix: "yes", // always applicable
    xx: "no",
    xxi: "no",
  });

  const clauseRows = [
    { key: "i", title: "(i) Property, Plant & Equipment and Intangible Assets" },
    { key: "ii", title: "(ii) Inventory" },
    { key: "iii", title: "(iii) Loans, investments, etc." },
    { key: "iv", title: "(iv) Section 185 and 186" },
    { key: "v", title: "(v) Public Deposits" },
    { key: "vi", title: "(vi) Cost records" },
    { key: "vii", title: "(vii) Statutory Dues" },
    { key: "viii", title: "(viii) Undisclosed Income" },
    { key: "ix", title: "(ix) Borrowings" },
    { key: "x", title: "(x) Issue of securities" },
    { key: "xi", title: "(xi) Fraud", forceYes: true },
    { key: "xii", title: "(xii) Nidhi Company" },
    { key: "xiii", title: "(xiii) Related parties" },
    { key: "xiv", title: "(xiv) Internal Audit" },
    { key: "xv", title: "(xv) Non-cash transactions", forceYes: true },
    { key: "xvi", title: "(xvi) NBFC" },
    { key: "xvii", title: "(xvii) Cash loss", forceYes: true },
    { key: "xviii", title: "(xviii) Resignation of statutory auditors" },
    { key: "xix", title: "(xix) Ability to pay liabilities", forceYes: true },
    { key: "xx", title: "(xx) CSR" },
    { key: "xxi", title: "(xxi) Component Auditors" },
  ];

  // Helper function to map modal responses to JSON format
  const mapResponsesToJSON = (responses: Record<string, string>): Record<string, string> => {
    const mapped: Record<string, string> = {};
    
    // Sort keys to ensure Q1, Q2, Q3... order
    const sortedKeys = Object.keys(responses).sort((a, b) => {
      const numA = parseInt(a.replace('q', ''), 10);
      const numB = parseInt(b.replace('q', ''), 10);
      return numA - numB;
    });

    sortedKeys.forEach((key) => {
      const questionNum = key.replace('q', '');
      const value = responses[key];
      
      // Map yes -> Yes, no -> No, empty -> ""
      if (value === "yes") {
        mapped[`Q${questionNum}`] = "Yes";
      } else if (value === "no") {
        mapped[`Q${questionNum}`] = "No";
      } else {
        mapped[`Q${questionNum}`] = "";
      }
    });

    return mapped;
  };

  // Helper function to map applicability
  const mapApplicability = (value: string): string => {
    if (value === "yes") return "Yes";
    if (value === "no") return "No";
    return "";
  };

  // Helper function to map JSON responses to modal format
  const mapJSONToModalResponses = (questions: Record<string, string>): Record<string, string> => {
    const modalResponses: Record<string, string> = {};
    Object.keys(questions).forEach((qKey) => {
      // Q1 -> q1, Q2 -> q2, etc.
      const qNum = qKey.replace('Q', '');
      const value = questions[qKey];
      if (value === "Yes") {
        modalResponses[`q${qNum}`] = "yes";
      } else if (value === "No") {
        modalResponses[`q${qNum}`] = "no";
      } else {
        modalResponses[`q${qNum}`] = "";
      }
    });
    return modalResponses;
  };

  // Helper function to map JSON applicability to state format
  const mapJSONApplicabilityToState = (value: string): string => {
    if (value === "Yes") return "yes";
    if (value === "No") return "no";
    return "no"; // default to no
  };

  // Load CARO Applicability on component mount
  useEffect(() => {
    const loadCAROApplicability = async () => {
      try {
        const downloadResult = await (window as any).sharePointAPI.downloadCaroApplicability();

        if (downloadResult.success && downloadResult.data && downloadResult.data.caro_applicability) {
          const caroApplicability = downloadResult.data.caro_applicability;

          // Map the JSON structure to state
          setApplicability({
            banking: mapJSONApplicabilityToState(caroApplicability["(i)"] || ""),
            insurance: mapJSONApplicabilityToState(caroApplicability["(ii)"] || ""),
            section8: mapJSONApplicabilityToState(caroApplicability["(iii)"] || ""),
            opc: mapJSONApplicabilityToState(caroApplicability["(iv)"] || ""),
            privateSmall: mapJSONApplicabilityToState(caroApplicability["(v)"] || ""),
          });

          console.log("Successfully loaded CARO Applicability from SharePoint");
        } else {
          console.log("CARO Applicability file not found or invalid, using defaults");
        }
      } catch (error) {
        console.error("Error loading CARO Applicability:", error);
        // Silently fail and use defaults
      }
    };

    loadCAROApplicability();
    checkExistingCAROReport();
  }, []); // Run only once on mount

  // Auto-trigger load on mount if applicable
  useEffect(() => {
    if (allNo && !checklistOpened) {
      loadCAROData();
    }
  }, [allNo, checklistOpened]);

  // Check for existing CARO Report
  const checkExistingCAROReport = async () => {
    try {
      const result = await (window as any).sharePointAPI?.loadDocumentListBySection?.("Conclusion_CARO_Report");
      if (result?.success && result.data && Array.isArray(result.data) && result.data.length > 0) {
        const firstFile = result.data[0];
        if (firstFile.name && firstFile.url) {
          setExistingCAROReport({ name: firstFile.name, url: firstFile.url });
        } else {
          setExistingCAROReport(null);
        }
      } else {
        setExistingCAROReport(null);
      }
    } catch (error) {
      console.error("Error checking for existing CARO Report:", error);
      setExistingCAROReport(null);
    }
  };

  // Load CARO data from SharePoint
  const loadCAROData = async () => {
    try {
      setLoadingCAROData(true);
      const downloadResult = await (window as any).sharePointAPI.downloadCaroJson();

      if (!downloadResult.success) {
        console.error("Failed to download CARO JSON:", downloadResult.error);
        alert(`Failed to load CARO data: ${downloadResult.error}`);
        setLoadingCAROData(false);
        return;
      }

      const caroData = downloadResult.data;

      if (!caroData || !caroData.CARO_Clauses) {
        console.error("Invalid CARO data structure");
        alert("Invalid CARO data structure");
        setLoadingCAROData(false);
        return;
      }

      // Map clause applicability
      const newClauseApplicability: Record<string, string> = {};
      const newClauseCompletionStatus: Record<string, boolean> = {};

      caroData.CARO_Clauses.forEach((clause: any) => {
        const clauseKey = clause.clause;
        newClauseApplicability[clauseKey] = mapJSONApplicabilityToState(clause.applicability || "");

        // Check if there are any responses
        const hasResponses = clause.questions && Object.keys(clause.questions).length > 0;
        // Check if responses are not all empty
        const isFilled = hasResponses && Object.values(clause.questions).some((val) => val !== "");

        newClauseCompletionStatus[clauseKey] = isFilled || false;
      });

      setClauseApplicability(newClauseApplicability);
      setClauseCompletionStatus(newClauseCompletionStatus);

      // Map clause responses
      caroData.CARO_Clauses.forEach((clause: any) => {
        const clauseKey = clause.clause;
        const modalResponses = mapJSONToModalResponses(clause.questions || {});
        
        if (clauseKey === "i") setClauseIResponses(modalResponses);
        else if (clauseKey === "ii") setClauseIIResponses(modalResponses);
        else if (clauseKey === "iii") setClauseIIIResponses(modalResponses);
        else if (clauseKey === "iv") setClauseIVResponses(modalResponses);
        else if (clauseKey === "v") setClauseVResponses(modalResponses);
        else if (clauseKey === "vi") setClauseVIResponses(modalResponses);
        else if (clauseKey === "vii") setClauseVIIResponses(modalResponses);
        else if (clauseKey === "viii") setClauseVIIIResponses(modalResponses);
        else if (clauseKey === "ix") setClauseIXResponses(modalResponses);
        else if (clauseKey === "x") setClauseXResponses(modalResponses);
        else if (clauseKey === "xi") setClauseXIResponses(modalResponses);
        else if (clauseKey === "xii") setClauseXIIResponses(modalResponses);
        else if (clauseKey === "xiii") setClauseXIIIResponses(modalResponses);
        else if (clauseKey === "xiv") setClauseXIVResponses(modalResponses);
        else if (clauseKey === "xv") setClauseXVResponses(modalResponses);
        else if (clauseKey === "xvi") setClauseXVIResponses(modalResponses);
        else if (clauseKey === "xvii") setClauseXVIIResponses(modalResponses);
        else if (clauseKey === "xviii") setClauseXVIIIResponses(modalResponses);
        else if (clauseKey === "xix") setClauseXIXResponses(modalResponses);
        else if (clauseKey === "xx") setClauseXXResponses(modalResponses);
      });

      setChecklistOpened(true);
      console.log("Successfully loaded CARO data from SharePoint");
    } catch (error) {
      console.error("Error loading CARO data:", error);
      alert(`Error loading CARO data: ${error instanceof Error ? error.message : "Unknown error"}`);
    } finally {
      setLoadingCAROData(false);
    }
  };

  // Upload CARO applicability JSON to SharePoint
  const uploadCAROApplicability = async () => {
    try {
      const caroApplicabilityData: Record<string, string> = {
        "(i)": applicability.banking === "yes" ? "Yes" : "No",
        "(ii)": applicability.insurance === "yes" ? "Yes" : "No",
        "(iii)": applicability.section8 === "yes" ? "Yes" : "No",
        "(iv)": applicability.opc === "yes" ? "Yes" : "No",
        "(v)": applicability.privateSmall === "yes" ? "Yes" : "No",
      };

      const jsonData = {
        caro_applicability: caroApplicabilityData
      };

      const jsonString = JSON.stringify(jsonData, null, 4);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

      const result = await (window as any).sharePointAPI.uploadFile({
        fileContent: base64Content,
        fileName: "Conclusion_CARO_Applicability.json",
        referenceValue: "",
        folderName: "juggernaut",
        fyYear: String(SHAREPOINT_FY_YEAR),
        sectionList: ["Conclusion_CARO_Applicability"]
      });

      if (result.success) {
        console.log("Successfully uploaded CARO applicability to SharePoint");
      } else {
        console.error("Failed to upload CARO applicability:", result.error);
        alert(`Failed to upload CARO applicability: ${result.error}`);
      }
    } catch (error) {
      console.error("Error uploading CARO applicability:", error);
      alert(`Error uploading CARO applicability: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  };

  // Save handler for CARO clause responses
  const handleSaveCAROResponses = async (clauseKey: string, responses: Record<string, string>) => {
    try {
      // Get applicability for this clause
      const applicability = mapApplicability(clauseApplicability[clauseKey] || "no");

      // Download current CARO JSON
      const downloadResult = await (window as any).sharePointAPI.downloadCaroJson();
      
      if (!downloadResult.success) {
        console.error("Failed to download CARO JSON:", downloadResult.error);
        alert(`Failed to download CARO JSON: ${downloadResult.error}`);
        return;
      }

      const caroData = downloadResult.data;

      // Map responses to JSON format
      const mappedQuestions = mapResponsesToJSON(responses);

      // Find and update the clause
      const clauseIndex = caroData.CARO_Clauses.findIndex(
        (clause: any) => clause.clause === clauseKey
      );

      if (clauseIndex === -1) {
        console.error(`Clause ${clauseKey} not found in CARO data`);
        alert(`Clause ${clauseKey} not found in CARO data`);
        return;
      }

      // Update the clause
      caroData.CARO_Clauses[clauseIndex].applicability = applicability;
      
      // Merge mapped questions with existing questions (preserve structure)
      if (clauseKey === "xxi") {
        caroData.CARO_Clauses[clauseIndex].questions = {};
      } else {
        // Preserve all question keys from the structure and update with new responses
        const existingQuestions = caroData.CARO_Clauses[clauseIndex].questions || {};
        const updatedQuestions: Record<string, string> = {};
        
        // Get all question keys (from existing structure or from responses)
        const allQuestionKeys = new Set([
          ...Object.keys(existingQuestions),
          ...Object.keys(mappedQuestions)
        ]);
        
        // Update each question key
        allQuestionKeys.forEach((qKey) => {
          if (mappedQuestions[qKey] !== undefined) {
            // Use the new response (mapped value)
            updatedQuestions[qKey] = mappedQuestions[qKey];
          } else if (existingQuestions[qKey] !== undefined) {
            // Keep existing value if no new response provided
            updatedQuestions[qKey] = existingQuestions[qKey];
          } else {
            // Initialize empty if neither exists
            updatedQuestions[qKey] = "";
          }
        });
        
        caroData.CARO_Clauses[clauseIndex].questions = updatedQuestions;
      }

      // Upload updated CARO JSON
      const uploadResult = await (window as any).sharePointAPI.uploadCaroJson(caroData);

      if (!uploadResult.success) {
        console.error("Failed to upload CARO JSON:", uploadResult.error);
        alert(`Failed to upload CARO JSON: ${uploadResult.error}`);
        return;
      }

      console.log(`Successfully saved CARO Clause ${clauseKey} responses to SharePoint`);
      
      // Update local state
      if (clauseKey === "i") setClauseIResponses(responses);
      else if (clauseKey === "ii") setClauseIIResponses(responses);
      else if (clauseKey === "iii") setClauseIIIResponses(responses);
      else if (clauseKey === "iv") setClauseIVResponses(responses);
      else if (clauseKey === "v") setClauseVResponses(responses);
      else if (clauseKey === "vi") setClauseVIResponses(responses);
      else if (clauseKey === "vii") setClauseVIIResponses(responses);
      else if (clauseKey === "viii") setClauseVIIIResponses(responses);
      else if (clauseKey === "ix") setClauseIXResponses(responses);
      else if (clauseKey === "x") setClauseXResponses(responses);
      else if (clauseKey === "xi") setClauseXIResponses(responses);
      else if (clauseKey === "xii") setClauseXIIResponses(responses);
      else if (clauseKey === "xiii") setClauseXIIIResponses(responses);
      else if (clauseKey === "xiv") setClauseXIVResponses(responses);
      else if (clauseKey === "xv") setClauseXVResponses(responses);
      else if (clauseKey === "xvi") setClauseXVIResponses(responses);
      else if (clauseKey === "xvii") setClauseXVIIResponses(responses);
      else if (clauseKey === "xviii") setClauseXVIIIResponses(responses);
      else if (clauseKey === "xix") setClauseXIXResponses(responses);
      else if (clauseKey === "xx") setClauseXXResponses(responses);

      // Update completion status
      const hasResponses = Object.keys(responses).length > 0;
      const isFilled = hasResponses && Object.values(responses).some((val) => val !== "");
      setClauseCompletionStatus((prev) => ({ ...prev, [clauseKey]: isFilled }));

      alert(`Successfully saved CARO Clause ${clauseKey} responses!`);
    } catch (error) {
      console.error("Error saving CARO responses:", error);
      alert(`Error saving CARO responses: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  };

  useEffect(() => {
    const loadUserEngagementRole = async () => {
      if (!currentEngagement || !user) {
        setUserEngagementRole(null);
        return;
      }
      try {
        const team = await apiService.getEngagementTeam(currentEngagement.id);
        const currentUserMember = team.find((member: any) => String(member.id) === String(user.id));
        setUserEngagementRole(currentUserMember?.role ?? null);
      } catch {
        setUserEngagementRole(null);
      }
    };
    loadUserEngagementRole();
  }, [currentEngagement, user]);

  const loadCardSignoffs = React.useCallback(async () => {
    try {
      const data = await loadMiscSignoffs();
      setCardSignoffs(getSignoffEntriesForCard(WORKSPACE_NAME, WORKSPACE_NAME, data.signoff));
    } catch {
      setCardSignoffs([]);
    }
  }, []);

  useEffect(() => {
    loadCardSignoffs();
  }, [loadCardSignoffs]);

  return (
    <div className="p-6 text-white space-y-6">
      <div className="flex items-center justify-end gap-2 mb-2 flex-wrap">
        <PlanningSignoffTokens signoffs={cardSignoffs} className="mt-0" />
        <Button
          variant="outline"
          size="sm"
          onClick={() => setSignoffDialogOpen(true)}
          className="border-white/20 text-white hover:bg-white/10"
        >
          <FileSignature className="h-4 w-4 mr-1.5" />
          Sign off
        </Button>
      </div>
      <PlanningSignoffDialog
        isOpen={signoffDialogOpen}
        onClose={() => setSignoffDialogOpen(false)}
        workspaceName={WORKSPACE_NAME}
        cardNames={[WORKSPACE_NAME]}
        user={user ? { user_name: (user as any).user_name ?? (user as any).name, type: (user as any).type } : null}
        userEngagementRole={userEngagementRole}
        onSignoffDone={loadCardSignoffs}
      />
      <div>
        <h2 className="text-xl font-semibold mb-1">CARO Applicability</h2>
        <p className="text-white/60 mb-4">Does the entity meet any of the below criteria?</p>

        <div className="rounded-lg border border-white/10">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-2/3">Criteria</TableHead>
                <TableHead className="w-1/3">Response</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell>Banking company</TableCell>
                <TableCell>
                  <Select
                    value={applicability.banking}
                    onValueChange={(v) => setApplicability((s) => ({ ...s, banking: v }))}
                  >
                    <SelectTrigger className="w-36">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Insurance company</TableCell>
                <TableCell>
                  <Select
                    value={applicability.insurance}
                    onValueChange={(v) => setApplicability((s) => ({ ...s, insurance: v }))}
                  >
                    <SelectTrigger className="w-36">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Section 8 company</TableCell>
                <TableCell>
                  <Select
                    value={applicability.section8}
                    onValueChange={(v) => setApplicability((s) => ({ ...s, section8: v }))}
                  >
                    <SelectTrigger className="w-36">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>One Person Company</TableCell>
                <TableCell>
                  <Select
                    value={applicability.opc}
                    onValueChange={(v) => setApplicability((s) => ({ ...s, opc: v }))}
                  >
                    <SelectTrigger className="w-36">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Private company (not being a holding or subsidiary of a public company) meeting ALL the following criteria:
                  <div className="text-xs text-white/60 mt-2 space-y-1">
                    <div>1. Paid up capital and reserves and surplus &lt;= Rs. 1 Crore (on Balance Sheet date)</div>
                    <div>2. Total borrowings from banks & financial institutions &lt;= Rs. 1 Crore (at any point of time during the financial year)</div>
                    <div>3. Total revenue &lt;= Rs. 10 Crore (during the financial year)</div>
                  </div>
                </TableCell>
                <TableCell>
                  <Select
                    value={applicability.privateSmall}
                    onValueChange={(v) => setApplicability((s) => ({ ...s, privateSmall: v }))}
                  >
                    <SelectTrigger className="w-36">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>

        <div className="mt-4 flex gap-3 items-center">
          {!allNo && (
            <div className="text-amber-300/90 text-sm">One or more criteria met. CARO Report will not be shown.</div>
          )}
          {allNo && !checklistOpened && (
            <Button
              className="bg-[#4da3ff]/20 text-[#a5d0ff] hover:bg-[#4da3ff]/30"
              onClick={loadCAROData}
              disabled={loadingCAROData}
              data-allow-archival-edit={true}
            >
              {loadingCAROData ? "Loading..." : "Open CARO Checklist"}
            </Button>
          )}
          <Button
            className="bg-[#4da3ff]/20 text-[#a5d0ff] hover:bg-[#4da3ff]/30"
            onClick={uploadCAROApplicability}
            disabled={isArchivalMode}
          >
            Save Checklist
          </Button>
        </div>
      </div>

      <Separator className="bg-white/10" />

      {allNo && checklistOpened && (
        <div>
          <h2 className="text-xl font-semibold mb-1">CARO Report</h2>
          <p className="text-white/60 mb-4">Set applicability and open checklists where relevant.</p>

          <div className="rounded-lg border border-white/10 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-2/3">CARO Clause</TableHead>
                  <TableHead className="w-1/6">Applicability</TableHead>
                  <TableHead className="w-1/6">Checklist</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {clauseRows.map((row) => {
                  const forced = row.forceYes === true;
                  const value = forced ? "yes" : clauseApplicability[row.key] ?? "no";
                  return (
                    <TableRow key={row.key}>
                      <TableCell>{row.title}</TableCell>
                      <TableCell>
                        <Select
                          value={value}
                          onValueChange={(v) =>
                            setClauseApplicability((s) => ({ ...s, [row.key]: v }))
                          }
                          disabled={forced}
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="yes">Yes</SelectItem>
                            <SelectItem value="no">No</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant={clauseCompletionStatus[row.key] ? "default" : "outline"}
                          className={clauseCompletionStatus[row.key]
                            ? "bg-green-500/20 text-green-300 hover:bg-green-500/30 border-green-500/30"
                            : "border-white/20 text-white/80 hover:bg-white/10"}
                          disabled={value !== "yes"}
                          onClick={() => {
                            if (row.key === "i") {
                              setClauseIChecklistOpen(true);
                            } else if (row.key === "ii") {
                              setClauseIIChecklistOpen(true);
                            } else if (row.key === "iii") {
                              setClauseIIIChecklistOpen(true);
                            } else if (row.key === "iv") {
                              setClauseIVChecklistOpen(true);
                            } else if (row.key === "v") {
                              setClauseVChecklistOpen(true);
                            } else if (row.key === "vi") {
                              setClauseVIChecklistOpen(true);
                            } else if (row.key === "vii") {
                              setClauseVIIChecklistOpen(true);
                            } else if (row.key === "viii") {
                              setClauseVIIIChecklistOpen(true);
                            } else if (row.key === "ix") {
                              setClauseIXChecklistOpen(true);
                            } else if (row.key === "x") {
                              setClauseXChecklistOpen(true);
                            } else if (row.key === "xi") {
                              setClauseXIChecklistOpen(true);
                            } else if (row.key === "xii") {
                              setClauseXIIChecklistOpen(true);
                            } else if (row.key === "xiii") {
                              setClauseXIIIChecklistOpen(true);
                            } else if (row.key === "xiv") {
                              setClauseXIVChecklistOpen(true);
                            } else if (row.key === "xv") {
                              setClauseXVChecklistOpen(true);
                            } else if (row.key === "xvi") {
                              setClauseXVIChecklistOpen(true);
                            } else if (row.key === "xvii") {
                              setClauseXVIIChecklistOpen(true);
                            } else if (row.key === "xviii") {
                              setClauseXVIIIChecklistOpen(true);
                            } else if (row.key === "xix") {
                              setClauseXIXChecklistOpen(true);
                            } else if (row.key === "xx") {
                              setClauseXXChecklistOpen(true);
                            }
                          }}
                          data-allow-archival-edit={true}
                        >
                          {clauseCompletionStatus[row.key] ? "Edit Checklist" : "Open Checklist"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          <div className="mt-6 flex justify-end">
            <Button
              className="bg-[#4da3ff]/20 text-[#a5d0ff] hover:bg-[#4da3ff]/30"
              onClick={async () => {
                try {
                  const result = await (window as any).sharePointAPI.generateCaroReport();

                  if (result.success) {
                    alert(result.message || "CARO Report generated and uploaded successfully!");
                    await checkExistingCAROReport();
                  } else {
                    alert(`Failed to generate CARO Report: ${result.error || "Unknown error"}`);
                  }
                } catch (error) {
                  console.error("Error generating CARO Report:", error);
                  alert(`Error generating CARO Report: ${error instanceof Error ? error.message : "Unknown error"}`);
                }
              }}
            >
              Generate CARO Report
            </Button>
          </div>

          {/* Display existing CARO Report if available */}
          {existingCAROReport && (
            <div className="mt-6 p-4 bg-white/5 border border-white/10 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileText className="h-5 w-5 text-[#4da3ff]" />
                  <div>
                    <div className="text-sm text-white/50">Existing CARO Report</div>
                    <div className="text-white font-medium">{existingCAROReport.name}</div>
                  </div>
                </div>
                <Button
                  onClick={async () => {
                    try {
                      // In archive mode, force download of the CARO report from the zipped client folder.
                      if (isArchivalMode) {
                        const sectionDocs = await (window as any).sharePointAPI?.loadDocumentListBySection?.(
                          "Conclusion_CARO_Report",
                        );
                        const matchedReport = Array.isArray(sectionDocs?.data)
                          ? sectionDocs.data.find(
                              (doc: { name?: string; url?: string }) =>
                                doc?.name === "Conclusion_CARO_Report.docx" && Boolean(doc?.url),
                            )
                          : null;
                        const downloadUrl = matchedReport?.url || existingCAROReport.url;
                        const downloadName = matchedReport?.name || "Conclusion_CARO_Report.docx";

                        if (!downloadUrl) {
                          alert("Conclusion_CARO_Report.docx was not found in archived client files.");
                          return;
                        }

                        const anchor = document.createElement("a");
                        anchor.href = downloadUrl;
                        anchor.download = downloadName;
                        anchor.rel = "noopener noreferrer";
                        anchor.click();
                        return;
                      }

                      if (existingCAROReport.url) {
                        if ((window as any).electronWindow?.openExternal) {
                          await (window as any).electronWindow.openExternal(existingCAROReport.url);
                        } else {
                          window.open(existingCAROReport.url, "_blank", "noopener,noreferrer");
                        }
                      }
                    } catch (error) {
                      console.error("Error opening/downloading CARO report:", error);
                      if (existingCAROReport.url) {
                        window.open(existingCAROReport.url, "_blank", "noopener,noreferrer");
                      }
                    }
                  }}
                  data-allow-archival-edit={true}
                  className="bg-[#4da3ff]/20 text-[#a5d0ff] hover:bg-[#4da3ff]/30 border border-[#4da3ff]/30"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Open CARO Report
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* CARO Clause I Checklist Modal */}
      <CAROClauseIChecklistModal
        isOpen={clauseIChecklistOpen}
        onClose={() => setClauseIChecklistOpen(false)}
        onSave={(responses) => handleSaveCAROResponses("i", responses)}
        initialResponses={clauseIResponses}
      />

      {/* CARO Clause II Checklist Modal */}
      <CAROClauseIIChecklistModal
        isOpen={clauseIIChecklistOpen}
        onClose={() => setClauseIIChecklistOpen(false)}
        onSave={(responses) => handleSaveCAROResponses("ii", responses)}
        initialResponses={clauseIIResponses}
      />

      {/* CARO Clause III Checklist Modal */}
      <CAROClauseIIIChecklistModal
        isOpen={clauseIIIChecklistOpen}
        onClose={() => setClauseIIIChecklistOpen(false)}
        onSave={(responses) => handleSaveCAROResponses("iii", responses)}
        initialResponses={clauseIIIResponses}
      />

      {/* CARO Clause IV Checklist Modal */}
      <CAROClauseIVChecklistModal
        isOpen={clauseIVChecklistOpen}
        onClose={() => setClauseIVChecklistOpen(false)}
        onSave={(responses) => handleSaveCAROResponses("iv", responses)}
        initialResponses={clauseIVResponses}
      />

      {/* CARO Clause V Checklist Modal */}
      <CAROClauseVChecklistModal
        isOpen={clauseVChecklistOpen}
        onClose={() => setClauseVChecklistOpen(false)}
        onSave={(responses) => handleSaveCAROResponses("v", responses)}
        initialResponses={clauseVResponses}
      />

      {/* CARO Clause VI Checklist Modal */}
      <CAROClauseVIChecklistModal
        isOpen={clauseVIChecklistOpen}
        onClose={() => setClauseVIChecklistOpen(false)}
        onSave={(responses) => handleSaveCAROResponses("vi", responses)}
        initialResponses={clauseVIResponses}
      />

      {/* CARO Clause VII Checklist Modal */}
      <CAROClauseVIIChecklistModal
        isOpen={clauseVIIChecklistOpen}
        onClose={() => setClauseVIIChecklistOpen(false)}
        onSave={(responses) => handleSaveCAROResponses("vii", responses)}
        initialResponses={clauseVIIResponses}
      />

      {/* CARO Clause VIII Checklist Modal */}
      <CAROClauseVIIIChecklistModal
        isOpen={clauseVIIIChecklistOpen}
        onClose={() => setClauseVIIIChecklistOpen(false)}
        onSave={(responses) => handleSaveCAROResponses("viii", responses)}
        initialResponses={clauseVIIIResponses}
      />

      {/* CARO Clause IX Checklist Modal */}
      <CAROClauseIXChecklistModal
        isOpen={clauseIXChecklistOpen}
        onClose={() => setClauseIXChecklistOpen(false)}
        onSave={(responses) => handleSaveCAROResponses("ix", responses)}
        initialResponses={clauseIXResponses}
      />

      {/* CARO Clause X Checklist Modal */}
      <CAROClauseXChecklistModal
        isOpen={clauseXChecklistOpen}
        onClose={() => setClauseXChecklistOpen(false)}
        onSave={(responses) => handleSaveCAROResponses("x", responses)}
        initialResponses={clauseXResponses}
      />

      {/* CARO Clause XI Checklist Modal */}
      <CAROClauseXIChecklistModal
        isOpen={clauseXIChecklistOpen}
        onClose={() => setClauseXIChecklistOpen(false)}
        onSave={(responses) => handleSaveCAROResponses("xi", responses)}
        initialResponses={clauseXIResponses}
      />

      {/* CARO Clause XII Checklist Modal */}
      <CAROClauseXIIChecklistModal
        isOpen={clauseXIIChecklistOpen}
        onClose={() => setClauseXIIChecklistOpen(false)}
        onSave={(responses) => handleSaveCAROResponses("xii", responses)}
        initialResponses={clauseXIIResponses}
      />

      {/* CARO Clause XIII Checklist Modal */}
      <CAROClauseXIIIChecklistModal
        isOpen={clauseXIIIChecklistOpen}
        onClose={() => setClauseXIIIChecklistOpen(false)}
        onSave={(responses) => handleSaveCAROResponses("xiii", responses)}
        initialResponses={clauseXIIIResponses}
      />

      {/* CARO Clause XIV Checklist Modal */}
      <CAROClauseXIVChecklistModal
        isOpen={clauseXIVChecklistOpen}
        onClose={() => setClauseXIVChecklistOpen(false)}
        onSave={(responses) => handleSaveCAROResponses("xiv", responses)}
        initialResponses={clauseXIVResponses}
      />

      {/* CARO Clause XV Checklist Modal */}
      <CAROClauseXVChecklistModal
        isOpen={clauseXVChecklistOpen}
        onClose={() => setClauseXVChecklistOpen(false)}
        onSave={(responses) => handleSaveCAROResponses("xv", responses)}
        initialResponses={clauseXVResponses}
      />

      {/* CARO Clause XVI Checklist Modal */}
      <CAROClauseXVIChecklistModal
        isOpen={clauseXVIChecklistOpen}
        onClose={() => setClauseXVIChecklistOpen(false)}
        onSave={(responses) => handleSaveCAROResponses("xvi", responses)}
        initialResponses={clauseXVIResponses}
      />

      {/* CARO Clause XVII Checklist Modal */}
      <CAROClauseXVIIChecklistModal
        isOpen={clauseXVIIChecklistOpen}
        onClose={() => setClauseXVIIChecklistOpen(false)}
        onSave={(responses) => handleSaveCAROResponses("xvii", responses)}
        initialResponses={clauseXVIIResponses}
      />

      {/* CARO Clause XVIII Checklist Modal */}
      <CAROClauseXVIIIChecklistModal
        isOpen={clauseXVIIIChecklistOpen}
        onClose={() => setClauseXVIIIChecklistOpen(false)}
        onSave={(responses) => handleSaveCAROResponses("xviii", responses)}
        initialResponses={clauseXVIIIResponses}
      />

      {/* CARO Clause XIX Checklist Modal */}
      <CAROClauseXIXChecklistModal
        isOpen={clauseXIXChecklistOpen}
        onClose={() => setClauseXIXChecklistOpen(false)}
        onSave={(responses) => handleSaveCAROResponses("xix", responses)}
        initialResponses={clauseXIXResponses}
      />

      {/* CARO Clause XX Checklist Modal */}
      <CAROClauseXXChecklistModal
        isOpen={clauseXXChecklistOpen}
        onClose={() => setClauseXXChecklistOpen(false)}
        onSave={(responses) => handleSaveCAROResponses("xx", responses)}
        initialResponses={clauseXXResponses}
      />
    </div>
  );
}
